import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import moment from 'moment';

import {
  OFFER_PRODUCT_SECTIONS,
  PRODUCT_STATUSES,
  CLAIM_PRODUCTS_SCOOPR,
  CLAIM_PRODUCTS,
} from '@config/products.config';
import {
  PRODUCTS_MAP,
  PRODUCTS_MAP_KLP,
  PRODUCTS_MAP_KOLIBRI,
  PRODUCTS_MAP_WATERCIRCLES,
  PRODUCTS_MAP_TTFINANS,
  PRODUCTS_MAP_STORM,
  PRODUCTS_MAP_SCOOPR
} from '../../config';

import { OfferApiService, SessionsApiService, UserApiService } from '@services/remote-api';
import { OfferInfo, OfferRoadmapStep } from '@features/profile/offer/offer-info/offer-info.interface';
import { TranslateService } from '@ngx-translate/core';
import { NbMenuItem } from '@nebular/theme';
import { ROUTES_MAP } from '@config/routes-config';
import { AccountService } from '@services/account.service';
import { DownloadFileService } from '@services/download-file.service';
import { WTC_EXCLUDED_VALIDATION_ERRORS_NO_MOTOR_BOAT, WTC_EXCLUDED_VALIDATION_ERRORS_SAILBOAT } from './profile.config';
import { jsPDF } from 'jspdf';

@Injectable()
export class ProfileService {
  productsMap: any;

  constructor(
    private sessionsApiService: SessionsApiService,
    private offerApiService: OfferApiService,
    private translateService: TranslateService,
    private userApiService: UserApiService,
    private accountService: AccountService,
    private downloadFileService: DownloadFileService
  ) {
    this.productsMap = this.fetchProductsMap();
  }

  getMenuItems(itemsType): NbMenuItem[] {
    switch (itemsType) {
      case 'MENU_ITEMS_SUPERADMIN': {
        return [
          // {
          //   title: this.translateService.instant('PROFILE_MENU.ANALYTICS'),
          //   link: `/${ROUTES_MAP.profile}/${ROUTES_MAP.analytics}`,
          // },
          {
            title: this.translateService.instant('PROFILE_MENU.OFFERS'),
            link: `/${ROUTES_MAP.profile}/${ROUTES_MAP.offerData}`,
            icon: 'clipboard'
          },
          {
            title: this.translateService.instant('PROFILE_MENU.INVITATIONS'),
            link: `/${ROUTES_MAP.profile}/${ROUTES_MAP.invitations}`,
            hidden: !this.accountService.getUser().showInvitations,
            icon: 'email'
          },
          {
            title: this.translateService.instant('PROFILE_MENU.SESSIONS'),
            link: `/${ROUTES_MAP.profile}/${ROUTES_MAP.sessions}`,
            icon: 'clock',
          },
          // {
          //   title: this.translateService.instant('PROFILE_MENU.TICKETS'),
          //   link: `/${ROUTES_MAP.profile}/${ROUTES_MAP.tickets}`,
          // },
          // {
          //   title: this.translateService.instant('PROFILE_MENU.SETTINGS'),
          //   link: `/${ROUTES_MAP.profile}/${ROUTES_MAP.settings}`,
          // },
          {
            title: this.translateService.instant('PROFILE_MENU.PRODUCT_VALIDATION'),
            link: `/${ROUTES_MAP.profile}/${ROUTES_MAP.productValidation}`,
            icon: 'options-2',
          },
          {
            title: this.translateService.instant('PROFILE_MENU.USERS'),
            link: `/${ROUTES_MAP.profile}/${ROUTES_MAP.users}`,
            icon: 'people',
          }
        ];
      }
      case 'MENU_ITEMS_ADMIN': {
        return [
          {
            title: this.translateService.instant('PROFILE_MENU.OFFERS'),
            link: `/${ROUTES_MAP.profile}/${ROUTES_MAP.offerData}`,
            icon: 'clipboard',
          },
          {
            title: this.translateService.instant('PROFILE_MENU.INVITATIONS'),
            link: `/${ROUTES_MAP.profile}/${ROUTES_MAP.invitations}`,
            hidden: !this.accountService.getUser().showInvitations,
            icon: 'email'
          },
        ];
      }
      case 'MENU_ITEMS_USER': {
        return [
          {
            title: this.translateService.instant('PROFILE_MENU.OFFERS'),
            link: `/${ROUTES_MAP.profile}/${ROUTES_MAP.offerData}`,
            icon: 'clipboard',
          }
        ];
      }
      default: return [];
    }
  }

  checkPropertiesForData(data): string {
    if (data !== undefined && data !== null) {
      if (Array.isArray(data)) {
        return 'array';
      }

      if (typeof data === 'object') {
        return 'object';
      }

      return 'regularData';
    } else {
      return '';
    }
  }

  getProductIcon(product: any): string {
    return this.productsMap[product.productType] && this.productsMap[product.productType].icon ?
      this.productsMap[product.productType].icon : '';
  }

  getPropertyValue(value: any): any {
    return this.translateService.instant(
      `${this.getPropertyValueMapListName()}.${value + ''.toUpperCase()}`).indexOf(this.getPropertyValueMapListName()) > -1 ?
      value : this.translateService.instant(`${this.getPropertyValueMapListName()}.${value + ''.toUpperCase()}`);
  }

  getPropertyValueMapListName(): string {
    return this.accountService.getUser().propertyValuesMapListName;
  }

  downloadFile(file) {
    file.downloaded = true;

    this.downloadFileService.downloadFile(file.url, file.fileName);
  }

  getPreviousCompanies(): Observable<string[]> {
    return this.offerApiService.getPreviousCompanies();
  }

  getHierarchyKonsepts(): Observable<string[]> {
    return this.offerApiService.getHierarchyKonsepts();
  }

  getHierarchyGrupperinger(konsept: string): Observable<string[]> {
    return this.offerApiService.getHierarchyGrupperinger(konsept);
  }

  getHierarchyTypes(konsept: string, gruppering: string): Observable<string[]> {
    return this.offerApiService.getHierarchyTypes(konsept, gruppering);
  }

  getHierarchyDefaults(): Observable<any> {
    return this.offerApiService.getHierarchyDefaults();
  }

  getScoreDiscountData(shortGuid: string): Observable<any> {
    return this.offerApiService.getScoreDiscountData(shortGuid);
  }

  changeScoreDiscountData(shortGuid: string): Observable<any> {
    return this.offerApiService.changeScoreDiscountData(shortGuid);
  }

  getOfferHistory(offerId: string): Observable<any> {
    return this.offerApiService.getOfferHistory(offerId);
  }

  getOfferEvents(offerId: string): Observable<any> {
    return this.offerApiService.getOfferEvents(offerId);
  }

  getStatusRoadmap(offerId: string): Observable<OfferRoadmapStep[]> {
    return this.offerApiService.getStatusRoadmap(offerId);
  }

  getOfferComments(offerId: string): Observable<any> {
    return this.offerApiService.getOfferComments(offerId);
  }

  getOfferDeclineReasons(shortGuid: string): Observable<any> {
    return this.offerApiService.getOfferDeclineReasons(shortGuid);
  }

  getSessionsData(filterData): Observable<any> {
    return this.sessionsApiService.getSessionsData(filterData);
  }

  getMyPageInvitationsData(filterData): Observable<any> {
    return this.sessionsApiService.getMyPageInvitationsData(filterData);
  }

  getAgentPrivateOfferText(shortGuid: string): Observable<any> {
    return this.offerApiService.getAgentPrivateOfferText(shortGuid);
  }

  getAgentCompaniesList(): Observable<any> {
    return this.userApiService.getAgentCompanies();
  }

  getUsersData(filterData): Observable<any> {
    return this.userApiService.getUsersData(filterData);
  }

  getOffersData(page: number, limit: number): Observable<any> {
    return this.offerApiService.getOffers(page, limit);
  }

  sendOfferEmail(offerId: number, data): Observable<any> {
    return this.offerApiService.sendOfferEmail(offerId, data);
  }

  getSessionInfo(sessionId: string): Observable<any> {
    return this.sessionsApiService.getSessionInfo(sessionId);
  }

  getOfferInfo(offerId: string): Observable<OfferInfo> {
    return this.offerApiService.getOfferInfo(offerId)
      .pipe(tap(this.handleOfferInfo.bind(this)));
  }

  getSessionFiles(sessionId: string): Observable<any> {
    return this.sessionsApiService.getSessionFiles(sessionId);
  }

  getOfferFiles(offerId: string): Observable<any> {
    return this.offerApiService.getOfferFiles(offerId);
  }

  getAllOfferFiles(offerId: string): Observable<any> {
    return this.offerApiService.getAllOfferFiles(offerId);
  }

  getOfferTableFilterData(filterData: any): Observable<any>  {
    return this.offerApiService.getOfferTableFilterData(filterData);
  }

  // getInvitationsTableFilterData(filterData: any): Observable<any>  {
  //   // return this.sessionsApiService.getMyPageInvitationsData(filterData);
  // }

  setOfferAssigneeUser(offerId, userData): Observable<any> {
    return this.offerApiService.setOfferAssigneeUser(offerId, userData);
  }

  editOfferStatus(offerId, data): Observable<any> {
    return this.offerApiService.editOfferStatus(offerId, data);
  }

  sendOfferSaleSession(data): Observable<any> {
    return this.offerApiService.setOfferSaleSession(data);
  }

  sendOfferNotificationSaleSession(offerId: string, data): Observable<any> {
    return this.offerApiService.sendOfferNotificationSaleSession(offerId, data);
  }

  sendOfferCompleteNotification(offerId: string, data): Observable<any> {
    return this.offerApiService.sendOfferCompleteNotification(offerId, data);
  }

  createUser(data): Observable<any> {
    return this.userApiService.createUser(data);
  }

  changeOfferComment(offerId: string, data): Observable<any> {
    return this.offerApiService.changeOfferComment(offerId, data);
  }

  changeOfferMetadata(offerId: string, data): Observable<any> {
    return this.offerApiService.changeOfferMetadata(offerId, data)
      .pipe(tap(this.handleOfferInfo.bind(this)));
  }

  editOfferSession(offerId: string, data: any, queryParams = {}): Observable<OfferInfo> {
    return this.offerApiService.editOfferSession(offerId, data, queryParams)
      .pipe(tap(this.handleOfferInfo.bind(this)));
  }

  editOfferProduct(offerId: string, data): Observable<any> {
    return this.offerApiService.editOfferProduct(offerId, data)
      .pipe(tap(this.handleOfferInfo.bind(this)));
  }

  editOffer(offerId: string, data: any): Observable<OfferInfo> {
    return this.offerApiService.editOffer(offerId, data)
      .pipe(tap(this.handleOfferInfo.bind(this)));
  }

  updateTiaPrice(offerId: string, data: any): Observable<OfferInfo> {
    return this.offerApiService.updateTiaPrice(offerId, data)
      .pipe(tap(this.handleOfferInfo.bind(this)));
  }

  fetchOfferPriceCalculation(offerId: string): Observable<any> {
    return this.offerApiService.fetchOfferPriceCalculation(offerId);
  }

  patchRandPrime(offerId: string, data: any): Observable<OfferInfo> {
    return this.offerApiService.patchRandPrime(offerId, data)
      .pipe(tap(this.handleOfferInfo.bind(this)));
  }

  saveEditProductTia(offerId: string, data): Observable<any> {
    return this.offerApiService.saveEditProductTia(offerId, data)
      .pipe(tap(this.handleOfferInfo.bind(this)));
  }

  addOfferProduct(offerId: string, data): Observable<any> {
    return this.offerApiService.addOfferProduct(offerId, data)
      .pipe(tap(this.handleOfferInfo.bind(this)));
  }

  deleteOfferProduct(offerId: string, data): Observable<any> {
    return this.offerApiService.deleteOfferProduct(offerId, data)
      .pipe(tap(this.handleOfferInfo.bind(this)));
  }

  focusAccept(offerId: string): Observable<OfferInfo> {
    return this.offerApiService.focusAccept(offerId)
      .pipe(tap(this.handleOfferInfo.bind(this)));
  }

  getOfvVariantsList(carRegNo: string, forceUseCarRegisterData: boolean): Observable<any> {
    return this.offerApiService.getOfvVariantsList(carRegNo, forceUseCarRegisterData);
  }

  getCarBrands(): Observable<any> {
    return this.offerApiService.getCarBrands();
  }

  getCarModels(brandId): Observable<any> {
    return this.offerApiService.getCarModels(brandId);
  }

  getCarModelRegYears(brandId, modelId): Observable<any> {
    return this.offerApiService.getCarModelRegYears(brandId, modelId);
  }

  getCarModelYears(brandId, modelId, regYearId): Observable<any> {
    return this.offerApiService.getCarModelYears(brandId, modelId, regYearId);
  }

  getCarModelVariant(brandId, modelId, regModelYearId, modelYearId): Observable<any> {
    return this.offerApiService.getCarModelVariant(brandId, modelId, regModelYearId, modelYearId);
  }

  isObjectsCompared(obj1, obj2): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }

  isTextHighlighted(): boolean {
    if (window.getSelection().toString().length === 0) {
      return false;
    }

    return true;
  }

  isCurrentProductStatus(product, productStatusToCheck): boolean {
    return product.config && product.config.productStatus && product.config.productStatus === PRODUCT_STATUSES[productStatusToCheck];
  }

  isProductExcluded(product): boolean {
    return product.config && product.config.productStatus && product.config.productStatus === PRODUCT_STATUSES.EXCLUDED;
  }

  isDefaultPropertyValue(propertyName: string, productData: any, arrayPropertyName = '', arrayPropertyIndex = ''): boolean {
    if (!arrayPropertyName) {
      return productData.defaultProperties && productData.defaultProperties.hasOwnProperty(propertyName);
    } else {
      return productData.properties &&
        productData.properties[propertyName][arrayPropertyIndex].defaultProperties &&
        productData.properties[propertyName][arrayPropertyIndex].defaultProperties.hasOwnProperty(arrayPropertyName);
    }
  }

  fetchProductsMap(): any {
    let productsMap = {};

    switch (this.accountService.getUser().propertiesKey) {
      case 'WATERCIRCLES': {
        productsMap = PRODUCTS_MAP_WATERCIRCLES;
        break;
      }
      case 'SCOOPR': {
        productsMap = PRODUCTS_MAP_SCOOPR;
        break;
      }
      case 'KLP': {
        productsMap = PRODUCTS_MAP_KLP;
        break;
      }
      case 'KOLIBRI': {
        productsMap = PRODUCTS_MAP_KOLIBRI;
        break;
      }
      case 'TTFINANS': {
        productsMap = PRODUCTS_MAP_TTFINANS;
        break;
      }
      case 'STORM': {
        productsMap = PRODUCTS_MAP_STORM;
        break;
      }
      default: {
        productsMap = PRODUCTS_MAP;
        break;
      }
    }

    return productsMap;
  }

  fetchClaimsMap(): any {
    let claimsMap = {};

    switch (this.accountService.getUser().customerData.name) {
      case 'SCOOPR': {
        claimsMap = CLAIM_PRODUCTS_SCOOPR;
        break;
      }
      default: {
        claimsMap = CLAIM_PRODUCTS;
        break;
      }
    }

    return claimsMap;
  }

  donwloadReceiptPDF(pdfContentId: string): void {
    const element = document.getElementById(pdfContentId);
    if (!element) {
      return;
    }

    const doc = new jsPDF('p', 'pt', 'a4');
    const fileName = `${this.translateService.instant('PDF_RECEIPT.HEADER_TITLE')}.pdf`;

    doc.setFont('helvetica');
    doc.setLanguage('no');
    doc.html(element, {
      callback: (pdf) => {
        pdf.save(fileName);
      },
    });
  }

  getAgeFromDOB(dobstr: string): any {
    return moment().diff(this.parseDOBDate(dobstr), 'years');
  }

  getDOBfromSsn(ssnStr: string): any {
    return ssnStr.substring(0, 6);
  }

  getPalicyDateBasedAge(dobstr: string, policyDateStr: string): any {
    const dobDate = this.parseDOBDate(dobstr);
    const policyDate = moment(policyDateStr, 'YYYY-MM-DD');

    return moment(policyDate).diff(dobDate, 'years');
  }

  private parseDOBDate(dobstr: string): any {
    const day = dobstr.substring(0, 2);
    const month = dobstr.substring(2, 4);
    let year = +dobstr.substring(4, 6);
    const centuryYearCheck = +moment().format('YYYY').substring(2, 4) - 5;

    if (year <= centuryYearCheck) {
      year += 2000;
    } else {
      year += 1900;
    }

    return moment(`${year}-${month}-${day}`, 'YYYY-MM-DD');
  }

  private handleOfferInfo(offerResponse: OfferInfo) {
    if (offerResponse.offerJson && offerResponse.offerJson.policies) {
      offerResponse.offerJson.policies.forEach(policy => {
        if (policy.products) {
          policy.products.forEach(product => {
            product = this.filterProductSection(product);

          });
        }
      });
    }
  }

  private filterProductSection(product: any): any {
    if (product.config && product.config.productStatus) {
      const productStatus = product.config.productStatus;

      switch (productStatus) {
        case PRODUCT_STATUSES.ADDED: {
          product.section = OFFER_PRODUCT_SECTIONS.CHANGED;
          break;
        }
        case PRODUCT_STATUSES.EDITED: {
          product.section = OFFER_PRODUCT_SECTIONS.CHANGED;
          break;
        }
        case PRODUCT_STATUSES.APPROVED: {
          product.section = OFFER_PRODUCT_SECTIONS.REGULAR;
          break;
        }
        case PRODUCT_STATUSES.EXCLUDED: {
          product.section = OFFER_PRODUCT_SECTIONS.REGULAR;
          break;
        }
      }

      if (this.productsMap[product.productType] && this.productsMap[product.productType].lifeInsuranse
          && !this.isCurrentProductStatus(product, 'ADDED')) {
        product.section = OFFER_PRODUCT_SECTIONS.REGULAR;
      }
    } else {
      product.section = OFFER_PRODUCT_SECTIONS.REGULAR;
    }

    // TODO: remove afer improve validation rules
    if (this.accountService.getUser().isWtcFlow) {
      if (product.productType === 'TRAVEL' && !product.properties.productPackage) {
        if (product.properties.travelExclusive === true) {
          product.properties.productPackage = this.translateService.instant('PROPERTIES_FORM.SELECT_LIST.PRODUCT_PACKAGE.ELITE');
        } else if (product.properties.travelExclusive === false) {
          product.properties.productPackage = this.translateService.instant('PROPERTIES_FORM.SELECT_LIST.PRODUCT_PACKAGE.STANDARD');
        }
      }

      if (product.productType === 'HOUSE') {
        // TODO: Remove after backend will be fixed
        product.properties.wtcBuildingConstructionFactor = false;
      }

      if (product.validationErrors && product.validationErrors.properties) {
        if (product.properties.wtcBoatGroup === 'NoMotorBoat') {
          Object.keys(product.validationErrors.properties).forEach(property => {
            if (WTC_EXCLUDED_VALIDATION_ERRORS_NO_MOTOR_BOAT.indexOf(property) > -1) {
              delete product.validationErrors.properties[property];
            }
          });
          if (!Object.keys(product.validationErrors.properties).length) {
            delete product.validationErrors.properties;
          }
        }

        if (product.properties.wtcBoatGroup === 'Sailboat') {
          Object.keys(product.validationErrors.properties).forEach(property => {
            if (WTC_EXCLUDED_VALIDATION_ERRORS_SAILBOAT.indexOf(property) > -1) {
              delete product.validationErrors.properties[property];
            }
          });
          if (!Object.keys(product.validationErrors.properties).length) {
            delete product.validationErrors.properties;
          }
        }
      }

    }
    // TODO: remove afer improve validation rules
  }

  private base64ToArrayBuffer(base64) {
    const binaryString = window.atob(base64);
    const binaryLen = binaryString.length;
    const bytes = new Uint8Array(binaryLen);
    for (let i = 0; i < binaryLen; i++) {
      const ascii = binaryString.charCodeAt(i);
      bytes[i] = ascii;
    }
    return bytes;
  }
}
